import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Monitor,
  Layers,
  HelpCircle,
  Lightbulb,
  ChevronDown,
  ChevronRight,
  Bell,
  Lock,
  Check,
  Download
} from 'lucide-react';
import { useAnnouncements } from '../contexts/AnnouncementContext';
import { phaseApi } from '../services/api';
import { useCockpitExplorer } from '../hooks/useCockpitExplorer';

// Phase grouping by order_index - matches the live 21-phase flow
const PHASE_GROUPS = [
  { name: 'GROUND', min: 1, max: 8 },
  { name: 'DEPARTURE', min: 9, max: 11 },
  { name: 'EN ROUTE', min: 12, max: 12 },
  { name: 'ARRIVAL', min: 13, max: 19 },
  { name: 'SHUTDOWN', min: 20, max: 21 },
];

const navLinkClasses = (isActive) =>
  `w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
    isActive
      ? 'bg-glass-current-row text-glass-cyan'
      : 'text-glass-text-muted hover:text-glass-text-main hover:bg-glass-card'
  }`;

/**
 * PhaseNavigation - Sidebar navigation for flight phases and cockpit exploration
 * Uses React Router Link for proper navigation
 * Highlights active phase based on current URL
 */
const PhaseNavigation = () => {
  const location = useLocation();
  const { unreadCount } = useAnnouncements();
  const [phases, setPhases] = useState([]);
  const { data: cockpitData } = useCockpitExplorer();
  const cockpitZones = cockpitData?.zones || [];

  // Fetch phases from API
  useEffect(() => {
    phaseApi.getAll()
      .then(res => setPhases(res.data))
      .catch(() => {});
  }, [location.pathname]);

  // Flight phases collapse state with localStorage persistence
  const [isPhasesCollapsed, setIsPhasesCollapsed] = useState(() => {
    const saved = localStorage.getItem('fsa_phases_collapsed');
    return saved === 'true';
  });

  // Persist collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('fsa_phases_collapsed', isPhasesCollapsed.toString());
  }, [isPhasesCollapsed]);

  // Determine active phase from URL
  const currentSlug = location.pathname.startsWith('/phases/')
    ? location.pathname.replace('/phases/', '')
    : null;

  const isCockpitExplore = location.pathname.startsWith('/cockpit');
  const currentCockpitSection = location.pathname.startsWith('/cockpit/')
    ? location.pathname.replace('/cockpit/', '')
    : null;

  const togglePhasesCollapse = () => {
    setIsPhasesCollapsed(prev => !prev);
  };

  const groupedPhases = PHASE_GROUPS.map(group => ({
    ...group,
    phases: phases.filter(p => p.order_index >= group.min && p.order_index <= group.max),
  })).filter(group => group.phases.length > 0);

  return (
    <nav className="space-y-1 py-2" data-testid="phase-navigation">
      {/* Explore the Cockpit Section - Independent reference module */}
      <div className="px-3 pt-2 pb-2 mb-1">
        <h3 className="font-plex-mono text-[10px] font-medium uppercase tracking-[0.12em] text-glass-text-faint">
          Explore the Cockpit
        </h3>
      </div>

      {/* Main Cockpit Landing Link */}
      <Link
        to="/cockpit"
        className={navLinkClasses(isCockpitExplore && !currentCockpitSection)}
        data-testid="cockpit-overview"
      >
        <Monitor className="w-4 h-4" strokeWidth={1.5} />
        <span className="truncate">Overview</span>
      </Link>

      {/* Cockpit Zone Links (from seeded explorer data) */}
      {cockpitZones.map((zone) => {
        const isActive = currentCockpitSection === zone.id;

        return (
          <Link
            key={zone.id}
            to={`/cockpit/${zone.id}`}
            className={`${navLinkClasses(isActive)} pl-6`}
            data-testid={`cockpit-${zone.id}`}
          >
            <Layers className="w-4 h-4" strokeWidth={1.5} />
            <span className="truncate">{zone.name}</span>
          </Link>
        );
      })}

      {/* Divider */}
      <div className="my-4 border-t border-glass-border-nav" />

      {/* Flight Phases - Collapsible Section */}
      <button
        onClick={togglePhasesCollapse}
        className="w-full px-3 pt-2 pb-2 mb-1 flex items-center justify-between cursor-pointer hover:bg-glass-card rounded-md transition-colors duration-150"
        data-testid="toggle-phases-collapse"
      >
        <span className="font-plex-mono text-[10px] font-medium uppercase tracking-[0.12em] text-glass-text-faint">
          Flight Phases
        </span>
        {isPhasesCollapsed ? (
          <ChevronRight className="w-3.5 h-3.5 text-glass-text-faint" strokeWidth={1.5} />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-glass-text-faint" strokeWidth={1.5} />
        )}
      </button>

      {/* Flight Phases List - grouped, with smooth collapse transition */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isPhasesCollapsed ? 'max-h-0 opacity-0' : 'max-h-[3000px] opacity-100'
        }`}
      >
        {groupedPhases.map((group) => (
          <div key={group.name} className="mb-2">
            <div className="px-3 py-1.5 font-plex-mono text-[10px] font-semibold tracking-[0.1em] text-glass-text-faint">
              {group.name}
            </div>
            {group.phases.map((phase) => {
              const isActive = currentSlug === phase.slug;
              const isLocked = phase.is_locked;
              const isDone = phase.is_complete && !isLocked;

              return (
                <Link
                  key={phase.id}
                  to={`/phases/${phase.slug}`}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150"
                  style={{
                    background: isActive ? 'var(--gc-current-row)' : 'transparent',
                    boxShadow: isActive ? 'inset 2px 0 0 var(--gc-cyan)' : 'none',
                  }}
                  data-testid={`phase-${phase.slug}`}
                >
                  {isLocked ? (
                    <span
                      className="w-5 font-plex-mono text-[11px] text-right shrink-0"
                      style={{ color: 'var(--gc-text-faint)' }}
                    >
                      {phase.order_index}
                    </span>
                  ) : (
                    <span
                      className="w-5 font-plex-mono text-[11px] text-right shrink-0"
                      style={{ color: isDone ? 'var(--gc-green)' : isActive ? 'var(--gc-cyan)' : 'var(--gc-text-faint)' }}
                    >
                      {phase.order_index}
                    </span>
                  )}
                  <span
                    className="truncate flex-1 font-barlow-condensed font-semibold text-[17px]"
                    style={{ color: isLocked ? 'var(--gc-text-muted)' : isActive ? 'var(--gc-text-main)' : isDone ? 'var(--gc-text-body)' : 'var(--gc-text-muted)' }}
                  >
                    {phase.title}
                  </span>
                  {isDone && (
                    <Check className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--gc-green)' }} />
                  )}
                  {isLocked && (
                    <Lock className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--gc-text-muted)' }} />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      {/* Divider */}
      <div className="my-4 border-t border-glass-border-nav" />

      {/* Help & Feedback Section */}
      <div className="px-3 pt-2 pb-2 mb-1">
        <h3 className="font-plex-mono text-[10px] font-medium uppercase tracking-[0.12em] text-glass-text-faint">
          Help & Feedback
        </h3>
      </div>

      {/* Support Link */}
      <Link
        to="/support"
        className={navLinkClasses(location.pathname === '/support')}
        data-testid="nav-support"
      >
        <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
        <span className="truncate">Support</span>
      </Link>

      {/* Downloads Link */}
      <Link
        to="/downloads"
        className={navLinkClasses(location.pathname === '/downloads')}
        data-testid="nav-downloads"
      >
        <Download className="w-4 h-4" strokeWidth={1.5} />
        <span className="truncate">Downloads</span>
      </Link>

      {/* Got an Idea Link */}
      <Link
        to="/ideas"
        className={navLinkClasses(location.pathname === '/ideas')}
        data-testid="nav-ideas"
      >
        <Lightbulb className="w-4 h-4" strokeWidth={1.5} />
        <span className="truncate">Got an Idea?</span>
      </Link>

      {/* What's New Link with Notification Badge */}
      <Link
        to="/whats-new"
        className={navLinkClasses(location.pathname === '/whats-new')}
        data-testid="nav-whats-new"
      >
        <div className="relative">
          <Bell className="w-4 h-4" strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-glass-cyan text-glass-cyan-on text-[10px] font-bold rounded-full flex items-center justify-center"
              data-testid="whats-new-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <span className="truncate">What's New</span>
      </Link>
    </nav>
  );
};

export { PHASE_GROUPS };
export default PhaseNavigation;
